import { useStore } from "../store";
import { DraggableOverlay } from "../util/DraggableOverlay";
import { useIsTouch } from "../util/useIsTouch";
import { useVoiceStore, muteKeyFor } from "./store";

/**
 * Compact roster panel: lists each remote player in the current room
 * with a per-player mute toggle. Mute key prefers wallet pubkey (so the
 * mute survives reload and applies in any room) and falls back to the
 * Colyseus sessionId for guest accounts.
 *
 * Hidden when voicechat is disabled on the server (no token issued).
 */
export function PlayerVoicePanel(): JSX.Element | null {
  const remotePlayers = useStore((s) => s.remotePlayers);
  const mutedKeys = useVoiceStore((s) => s.mutedKeys);
  const tokenInfo = useVoiceStore((s) => s.tokenInfo);
  const activeSpeakers = useVoiceStore((s) => s.activeSpeakers);
  const isTouch = useIsTouch();

  if (!tokenInfo) return null;
  const list = Array.from(remotePlayers.values());
  if (list.length === 0) return null;

  // Desktop: bottom-right (chat lives bottom-left, no conflict).
  // Touch: stack in the top-right voice column below SpeakerList — chat
  // docks bottom-right on touch (EcctrlJoystick owns bottom-left), so we
  // can't live there. Draggable on touch.
  const anchor = isTouch
    ? { right: 12, top: 360, safeAreaTop: true }
    : { right: 12, bottom: 12 };

  return (
    <DraggableOverlay
      id="voice-player-panel"
      defaultAnchor={anchor}
      enabled={isTouch}
      zIndex={12}
      nonDragSelector="button"
    >
    <div
      style={{
        padding: 8,
        background: "rgba(15,16,24,0.92)",
        border: "1px solid #2a2a36",
        borderRadius: 6,
        color: "#e8e8ef",
        fontSize: 12,
        minWidth: 180,
        maxWidth: 260,
      }}
    >
      <div
        style={{
          color: "#9aa0b4",
          fontSize: 11,
          marginBottom: 4,
          fontWeight: 600,
          textTransform: "uppercase",
          letterSpacing: 0.5,
        }}
      >
        Voice · {list.length}
      </div>
      {list.map((p) => {
        const key = muteKeyFor(p.pubkey, p.id);
        const muted = mutedKeys.has(key);
        const speaking = activeSpeakers.has(p.id);
        return (
          <div
            key={p.id}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 6,
              padding: "2px 0",
            }}
          >
            <span
              aria-hidden
              style={{
                display: "inline-block",
                width: 8,
                height: 8,
                borderRadius: 4,
                background: speaking ? "#7df0a8" : "#2a2a36",
                boxShadow: speaking ? "0 0 6px #7df0a8" : "none",
                transition: "background 80ms",
              }}
            />
            <span
              style={{
                flex: 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                color: muted ? "#888" : "#e8e8ef",
                textDecoration: muted ? "line-through" : "none",
              }}
            >
              {p.displayName || p.id.slice(0, 6)}
            </span>
            <button
              onClick={() => useVoiceStore.getState().togglePlayerMute(key)}
              title={muted ? "Unmute" : "Mute"}
              style={{
                background: muted ? "#3a1414" : "transparent",
                color: muted ? "#ffb0b0" : "#cfd2dd",
                border: "1px solid #2a2a36",
                borderRadius: 4,
                padding: "1px 6px",
                cursor: "pointer",
                fontSize: 12,
                lineHeight: 1,
              }}
            >
              {muted ? "🔇" : "🎤"}
            </button>
          </div>
        );
      })}
    </div>
    </DraggableOverlay>
  );
}
